import { getApiBaseUrl } from '../../config/runtimeConfig';
import { sessionFetch } from '../../services/session';


function httpError(message, status) {
  const error = new Error(message);
  error.status = status;
  return error;
}

async function request(path, options = {}) {
  let response;
  try {
    response = await sessionFetch(`${getApiBaseUrl()}${path}`, {
      credentials: 'include',
      ...options,
      headers: { Accept: 'application/json', ...options.headers },
      signal: AbortSignal.timeout(20000),
    });
  } catch (error) {
    throw httpError(error.name === 'TimeoutError'
      ? 'The request timed out. Please try again.'
      : 'Unable to connect. Please check your connection and try again.');
  }

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw httpError(
      typeof data?.message === 'string' ? data.message : 'Unable to complete the inventory request.',
      response.status,
    );
  }
  return data;
}

export function isUnpersistedInventoryError(error) {
  return !error?.status || error.status === 404;
}

function toProduct(row) {
  return {
    id: row.id,
    product_id: row.product_id,
    name: row.name || '',
    quantity: Number(row.quantity) || 0,
    unit_id: row.unit_id == null ? null : Number(row.unit_id),
    cbm: Number(row.cbm) || 0,
    net_weight: Number(row.net_weight) || 0,
    gross_weight: Number(row.gross_weight) || 0,
  };
}

export function toInventory(row) {
  if (!row) return null;
  return {
    id: row.id,
    measurement_level: row.measurement_level || 'Carton',
    warehouse_id: row.warehouse_id,
    warehouse_name: row.warehouse_name || '',
    warehouse_code: row.warehouse_code || '',
    warehouse_city: row.warehouse_city || '',
    customer_id: row.customer_id,
    customer_name: row.customer_name || '',
    customer_logo: row.customer_logo || '',
    priority_id: row.priority_id,
    priority_name: row.priority_name || '',
    marko: row.marko || '',
    inventory_date: row.inventory_date || '',
    reference_number: row.reference_number || '',
    status: row.status || 'Available',
    packaging_type_id: row.packaging_type_id,
    packaging_type: row.packaging_type || '',
    packaging_quantity: Number(row.packaging_quantity) || 0,
    total_carton_qty: Number(row.total_carton_qty) || 0,
    net_weight: Number(row.net_weight) || 0,
    gross_weight: Number(row.gross_weight) || 0,
    unit_id: row.unit_id == null ? null : Number(row.unit_id),
    batch_quantity: Number(row.batch_quantity) || 0,
    batch_number: row.batch_number || '',
    cbm: Number(row.cbm) || 0,
    products: Array.isArray(row.products) ? row.products.map(toProduct) : [],
    packaging: Array.isArray(row.packaging) ? row.packaging.map(pack => ({
      id: pack.id,
      measurement_level: pack.measurement_level || row.measurement_level || 'Carton',
      use_master_measurements: pack.use_master_measurements === true,
      rawMeasurements: pack.rawMeasurements,
      cbm: Number(pack.cbm) || 0, net_weight: Number(pack.net_weight) || 0, gross_weight: Number(pack.gross_weight) || 0,
      packaging_type_id: pack.packaging_type_id,
      packaging_type: pack.packaging_type || '',
      packaging_quantity: Number(pack.packaging_quantity) || 0,
      total_carton_qty: Number(pack.total_carton_qty) || 0,
      batches: Array.isArray(pack.batches) ? pack.batches.map(batch => ({
        id: batch.id,
        rawMeasurements: batch.rawMeasurements,
        priority_id: batch.priority_id == null ? null : Number(batch.priority_id),
        priority_name: batch.priority_name || '',
        batch_number: batch.batch_number || '',
        batch_quantity: Number(batch.batch_quantity) || 0,
        unit_id: batch.unit_id == null ? null : Number(batch.unit_id),
        cbm: Number(batch.cbm) || 0,
        net_weight: Number(batch.net_weight) || 0,
        gross_weight: Number(batch.gross_weight) || 0,
        products: Array.isArray(batch.products) ? batch.products.map(toProduct) : [],
      })) : [],
    })) : [],
    is_deleted: false,
  };
}

function buildListQuery(filters = {}) {
  const params = new URLSearchParams();
  if (filters.query) params.set('query', filters.query);
  if (filters.warehouse_id) params.set('warehouse_id', String(filters.warehouse_id));
  if (filters.customer_id) params.set('customer_id', String(filters.customer_id));
  if (filters.status) params.set('status', filters.status);
  params.set('page', String(filters.page || 1));
  params.set('pageSize', String(filters.pageSize || 6));
  const qs = params.toString();
  return qs ? `/inventory?${qs}` : '/inventory';
}

// Share identical pending requests only; settled entries never cache data.
const inventoryInFlight = new Map();
function shareInventoryRequest(path, load) {
  const key = `${getApiBaseUrl()}${path}`;
  if (!inventoryInFlight.has(key)) {
    const pending = load(path).finally(() => {
      if (inventoryInFlight.get(key) === pending) inventoryInFlight.delete(key);
    });
    inventoryInFlight.set(key, pending);
  }
  return inventoryInFlight.get(key);
}

export function listInventory(filters, options = {}) {
  return shareInventoryRequest(buildListQuery(filters), path => loadInventory(path, options));
}

export function getInventorySummary(filters = {}, module = 'Inventory', options = {}) {
  const params = new URLSearchParams();
  if (filters.query) params.set('query', filters.query);
  if (filters.warehouse_id) params.set('warehouse_id', String(filters.warehouse_id));
  if (filters.customer_id) params.set('customer_id', String(filters.customer_id));
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();
  const path = module === 'Dashboard' ? '/dashboard/inventory-summary' : '/inventory/summary';
  return shareInventoryRequest(qs ? `${path}?${qs}` : path, address => loadInventorySummary(address, options));
}

async function loadInventory(path, options) {
  try {
    const data = await request(path, options);
    return {
      inventory: (data.inventory || []).map(toInventory),
      total: Number(data.total) || 0,
    };
  } catch (error) {
    if (isUnpersistedInventoryError(error)) return { inventory: [], total: 0 };
    throw error;
  }
}

async function loadInventorySummary(path, options) {
  try {
    return await request(path, options);
  } catch (error) {
    if (isUnpersistedInventoryError(error)) {
      return {
        inventory_records: 0,
        unique_products: 0,
        warehouse_capacity: 0,
        free_capacity: 0,
        total_cartons: 0,
        total_products: 0,
        net_weight: 0,
        gross_weight: 0,
        lookups: { warehouses: [], customers: [], products: [], packaging_types: [], priorities: [], units: [], statuses: [] },
      };
    }
    throw error;
  }
}

export async function getInventory(id, options = {}) {
  const data = await request(`/inventory/${id}`, options);
  return data.inventory ? toInventory(data.inventory) : null;
}

function toPayload(payload) {
  const shared = {
    measurement_level: payload.measurement_level,
    warehouse_id: payload.warehouse_id,
    customer_id: payload.customer_id,
    ...(payload.customerName ? { customerName: payload.customerName } : {}),
    priority_id: payload.priority_id,
    marko: payload.marko,
    reference_number: payload.reference_number,
    inventory_date: payload.inventory_date,
    status: payload.status,
  };
  if (Array.isArray(payload.packaging) && payload.packaging.length) {
    return { ...shared, packaging: payload.packaging };
  }
  return {
    ...shared,
    packaging_type_id: payload.packaging_type_id,
    packaging_quantity: payload.packaging_quantity,
    total_carton_qty: payload.total_carton_qty,
    batch_number: payload.batch_number,
    batch_quantity: payload.batch_quantity,
    unit_id: payload.unit_id,
    cbm: payload.cbm,
    net_weight: payload.net_weight,
    gross_weight: payload.gross_weight,
    products: payload.products,
  };
}

export async function createInventory(payload) {
  const data = await request('/inventory', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(toPayload(payload)),
  });
  const inventory = data.inventory;
  if (!inventory?.id) throw httpError('Inventory was not saved to the database.');
  return toInventory(inventory);
}

export async function updateInventory(id, payload) {
  const data = await request(`/inventory/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(toPayload(payload)),
  });
  const inventory = data.inventory;
  if (!inventory?.id) throw httpError('Inventory was not saved to the database.');
  inventoryInFlight.clear();
  return toInventory(inventory);
}

export async function deleteInventory(id) {
  await request(`/inventory/${id}`, { method: 'DELETE' });
}
